import { Card, Checkbox, Divider } from '@mui/material';
import type { SxProps } from '@mui/system';
import { Stack } from '@mui/system';
import { useMutation } from '@tanstack/react-query';
import { EventTicketsAccordion, useIsMobile, showToast, IconButton } from '@tix-ticketing/ui-components';
import { CrossIcon } from '@tix-ticketing/ui-icons/outline';
import { useRouter } from 'next/router';
import { useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { deleteApiOrdersByEncryptedOrderIdDonationsByOrderDonationId, deleteApiOrdersByEncryptedOrderIdEventByEventId, deleteApiOrdersByEncryptedOrderIdGiftcardsByGiftCardId, deleteApiOrdersByEncryptedOrderIdMembershipsByOrderMembershipId, deleteApiOrdersByEncryptedOrderIdTickets, getApiOrdersByEncryptedOrderId, patchApiTicketsByEncryptedTicketIdTicketTypeByTicketTypeId } from 'api/generated';
import { getApiEventsByEventIdQueryKey, getApiEventsByEventIdSeatsQueryKey } from 'api/generated/@tanstack/react-query.gen';
import type { EventDto, EventListItemDto, OrderResponse, SeatsResponse, SubscriptionTypeGroupDto, TicketDto } from 'api/generated/types.gen';
import { DiscountClubMark } from 'components/cart/cart-discount-clubs';
import { CartEventActions } from 'components/cart/cart-event-actions';
import { CartEventProducts } from 'components/cart/cart-event-products';
import { CartAsideEventCardBody } from 'components/common/cards/cart-aside-event-card-body';
import { EventCardBody } from 'components/common/cards/event-card-body';
import { CartExtraOrderItem } from 'components/common/cart/cart-extra-order-item';
import { CartSubscriptionCard } from 'components/common/cart/cart-subscription-card';
import { staticCardSx } from 'components/common/cart/static-card-sx';
import { StickyCardHeader, stickyCardSx } from 'components/common/cart/sticky-card-header';
import { DeleteConfirmationDialog } from 'components/common/dialogs/delete-confirmation-dialog';
import { Seat360Dialog } from 'components/common/dialogs/seat-360-dialog';
import { useAppState } from 'lib/app-state';
import { useOrderRoutes } from 'lib/dedicated-order';
import { toastError, unexpectedErrorToast } from 'lib/helpers/error-toast-helpers';
import { getExchangeTicketTypeOptions } from 'lib/helpers/exchange-helpers';
import { useCurrencyValue } from 'lib/helpers/formatted-price';
import { getAllOrderTickets, getEventTickets, getEventTicketsOriginalTotal, getEventTicketsTotal, getMergedSubscriptionGroups, mapOrderTicketGroups } from 'lib/helpers/order-helpers';
import { getAllItemKeys, giftCardKey, membershipKey, ticketKey } from 'lib/helpers/pay-selection-helpers';
import { stripHtmlTags } from 'lib/helpers/string-helpers';
import type { PaySelection } from 'lib/hooks/use-pay-selection';
import type { ReservationRemovals } from 'lib/hooks/use-reservation-removals';
import { queryClient } from 'lib/tanstack-query-client';

/** Every order edit made here runs under this key, so a page can tell one is
 *  still on its way — a ticket removal shows before the backend has it. */
export const cartOrderMutationKey = ['cartOrder'];
export const CartOrder = ({
  inAside,
  order: orderProp,
  onOrderChange,
  allowViewSeats = true,
  allowRemoveTickets = true,
  allowRemoveGroups = true,
  onRemoveIntercepted,
  onCancelOrder,
  selection,
  removals,
  sx
}: {
  inAside?: boolean;
  /** Order to render. Defaults to the order held in app state. */
  order?: OrderResponse;
  /**
   * Receives the order after every change made here (undefined once the order
   * is emptied). Required alongside `order`; without it the changes are written
   * back to app state.
   */
  onOrderChange?: (order: OrderResponse | undefined) => void;
  /** Whether the seat picker can be opened from a ticket group. */
  allowViewSeats?: boolean;
  /** Whether individual tickets can be taken off the order. */
  allowRemoveTickets?: boolean;
  /**
   * Whether a whole item can be taken off the order: an event with all its
   * tickets, a subscription, a gift card, a membership or a donation. Each has
   * its own endpoint and none of them is covered by `allowRemoveTickets`.
   */
  allowRemoveGroups?: boolean;
  /**
   * Takes over every removal: nothing is removed here and this is called
   * instead. An exchange uses it to confirm that removing tickets drops the
   * change, its ticket count being fixed by the tickets handed back.
   */
  onRemoveIntercepted?: () => void;
  /**
   * Called instead of removing the last subscription left on the order. A
   * renewal is about its subscriptions, so taking off the last one cancels it.
   */
  onCancelOrder?: () => void;
  /**
   * Live while the cart is running a select-items-to-pay selection. Every
   * splittable item grows a checkbox and removal is suppressed, so the buyer is
   * only ever ticking rather than deleting.
   */
  selection?: PaySelection;
  /**
   * Live while the cart holds a reservation opened from My pages. Removing an
   * item then only takes it off the cart's copy of the order — the reservation
   * goes on holding it until checkout.
   */
  removals?: ReservationRemovals;
  sx?: SxProps;
}) => {
  const intl = useIntl();
  const isMobile = useIsMobile();
  const cardPadding = inAside ? 2 : 3;
  const router = useRouter();
  const routes = useOrderRoutes();
  const appState = useAppState();
  const {
    eventData
  } = appState;
  const isSoftRemove = !!removals?.isActive;
  // While the reservation is being edited the cart renders what would be left
  // after the removals, so a removed item disappears as it would anywhere else.
  const order = isSoftRemove ? removals?.keptOrder : orderProp ?? eventData.order;
  const allEvents = order?.events || [];
  // The API returns one subscription group per purchased seat-set; merge them
  // by subscription type so each subscription renders as a single card with all
  // its tickets grouped under their events.
  const subscriptionGroups = getMergedSubscriptionGroups(order);
  const giftCards = order?.giftCards || [];
  const memberships = order?.memberships || [];
  const donations = order?.donations || [];

  // Event ids that belong to a subscription are rendered inside the
  // subscription card, so exclude them from the top-level events list to
  // avoid double-rendering them as standalone cards.
  const subscriptionEventIds = new Set(subscriptionGroups.flatMap(group => (group.eventTicketGroups ?? []).map(etg => etg.eventId)));
  const events = allEvents.filter(event => !subscriptionEventIds.has(event.id));
  const [show360Ticket, setShow360Ticket] = useState(null as TicketDto | null);
  const [pendingRemoval, setPendingRemoval] = useState<{
    type: 'giftCard' | 'membership' | 'event' | 'donation' | 'subscription';
    id: number;
  } | null>(null);
  const pendingItemTypeRef = useRef<'giftCard' | 'membership' | 'event' | 'donation' | 'subscription'>('giftCard');
  if (pendingRemoval) {
    pendingItemTypeRef.current = pendingRemoval.type;
  }
  const currencyValue = useCurrencyValue();
  const isSelecting = !!selection?.isSelecting;
  const canRemoveTickets = allowRemoveTickets && !isSelecting;
  const canRemoveGroups = allowRemoveGroups && !isSelecting;
  // The hall would show seats the cart has hidden, so it is closed off until
  // the removals have been committed one way or the other.
  const canViewSeats = !isSelecting && !removals?.hasRemovals;
  // Adding products leaves the cart, and every change replaces the order,
  // either of which would drop a half-finished selection or reservation edit.
  const allowProductChanges = !isSelecting && !isSoftRemove;
  const ticketSelectLabel = intl.formatMessage({
    id: "cart.selectTicket",
    defaultMessage: [{
      "type": 0,
      "value": "Select ticket"
    }]
  });
  const previousOrderRef = useRef<OrderResponse | undefined>(undefined);

  // Every change made here flows through this: an owner passing its own order
  // takes the update, otherwise it lands in app state next to its event.
  const applyOrder = (next: OrderResponse | undefined) => {
    if (onOrderChange) {
      onOrderChange(next);
      return;
    }
    appState.setEventData({
      event: next ? appState.eventData.event : undefined,
      order: next
    });
  };

  // Every removal made here goes through this, so an owner that has to confirm
  // something first (an exchange, which a removal cancels) gets asked instead.
  const handleRemove = (remove: () => void) => {
    if (onRemoveIntercepted) {
      onRemoveIntercepted();
      return;
    }
    remove();
  };
  const removeEventMutation = useMutation({
    mutationKey: cartOrderMutationKey,
    mutationFn: (eventId: number) => deleteApiOrdersByEncryptedOrderIdEventByEventId({
      path: {
        encryptedOrderId: order?.encryptedId ?? '0',
        eventId
      }
    }),
    onSuccess: (data: any) => {
      setPendingRemoval(null);
      if (data.status === 204) {
        applyOrder(undefined);
      } else if (data.status !== 200) {
        toastError(data.response.data);
      } else {
        applyOrder(data.data as OrderResponse);
      }
    }
  });
  const removeTicketFromOrder = (order: OrderResponse | undefined, ticket: TicketDto): OrderResponse | undefined => {
    if (!order) return order;
    const updatedOrder = mapOrderTicketGroups(order, tickets => tickets.filter(t => t.id !== ticket.id));
    const hasTicketsLeft = getAllOrderTickets(updatedOrder).length > 0;
    if (!hasTicketsLeft) return undefined;
    return updatedOrder;
  };
  const removeTicketMutation = useMutation({
    mutationKey: cartOrderMutationKey,
    mutationFn: ({
      ticket
    }: {
      ticket: TicketDto;
    }) => {
      previousOrderRef.current = order;
      applyOrder(removeTicketFromOrder(order, ticket));
      return deleteApiOrdersByEncryptedOrderIdTickets({
        path: {
          encryptedOrderId: previousOrderRef.current?.encryptedId ?? '0'
        },
        body: {
          ticketIds: [ticket.id as number]
        }
      });
    },
    onSuccess: (data: any) => {
      if (data.status === 204) {
        applyOrder(undefined);
      } else if (data.status !== 200) {
        toastError(data.response.data);
        applyOrder(previousOrderRef.current);
      } else {
        applyOrder(data.data as OrderResponse);
      }
    },
    onError: () => {
      unexpectedErrorToast();
      applyOrder(previousOrderRef.current);
    }
  });
  const changeTicketTypeMutation = useMutation({
    mutationKey: cartOrderMutationKey,
    mutationFn: async ({
      encryptedTicketId,
      ticketTypeId
    }: {
      encryptedTicketId: string;
      ticketTypeId: number;
    }) => {
      const encryptedOrderId = order?.encryptedId ?? undefined;
      const patched: any = await patchApiTicketsByEncryptedTicketIdTicketTypeByTicketTypeId({
        path: {
          encryptedTicketId,
          ticketTypeId
        },
        query: {
          encryptedOrderId
        }
      });

      // A type change re-runs the basket rules, which can re-price the other tickets and move the
      // order's charges, so the settled order is read back in full.
      const settled: any = patched.status === 200 && encryptedOrderId ? await getApiOrdersByEncryptedOrderId({
        path: {
          encryptedOrderId
        }
      }) : undefined;
      return {
        patched,
        settled
      };
    },
    onSuccess: ({
      patched,
      settled
    }: {
      patched: any;
      settled: any;
    }, variables: {
      encryptedTicketId: string;
      ticketTypeId: number;
    }) => {
      if (patched.status !== 200) {
        toastError(patched.response.data);
        return;
      }
      if (!order) return;
      const updatedTicket = patched.data as TicketDto;

      // The read-back can fail after the change has committed, so the ticket the API returned still
      // goes in and the order-level figures settle on the next mutation.
      applyOrder(settled?.status === 200 ? settled.data as OrderResponse : mapOrderTicketGroups(order, tickets => tickets.map(t => t.encryptedId === variables.encryptedTicketId ? updatedTicket : t)));

      // A basket rule can hold a ticket on a type of its own, overriding the
      // selection, so the buyer is told rather than watching it revert.
      if (updatedTicket.ticketTypeId !== variables.ticketTypeId) {
        showToast({
          title: intl.formatMessage({
            id: "cart.ticketTypeSetByRuleTitle",
            defaultMessage: [{
              "type": 0,
              "value": "Ticket type changed"
            }]
          }),
          message: intl.formatMessage({
            id: "cart.ticketTypeSetByRule",
            defaultMessage: [{
              "type": 0,
              "value": "A discount on this order set this ticket to "
            }, {
              "type": 1,
              "value": "ticketType"
            }, {
              "type": 0,
              "value": "."
            }]
          }, {
            ticketType: updatedTicket.ticketTypeName ?? ''
          }),
          severity: 'info'
        });
      }
    },
    onError: () => {
      unexpectedErrorToast();
    }
  });
  const removeGiftCardMutation = useMutation({
    mutationKey: cartOrderMutationKey,
    mutationFn: (giftCardId: number) => deleteApiOrdersByEncryptedOrderIdGiftcardsByGiftCardId({
      path: {
        encryptedOrderId: order?.encryptedId ?? '0',
        giftCardId
      }
    }),
    onSuccess: (data: any) => {
      setPendingRemoval(null);
      if (data.status === 204) {
        applyOrder(undefined);
      } else if (data.status !== 200) {
        toastError(data.response.data);
      } else {
        applyOrder(data.data);
      }
    }
  });
  const removeMembershipMutation = useMutation({
    mutationKey: cartOrderMutationKey,
    mutationFn: (orderMembershipId: number) => deleteApiOrdersByEncryptedOrderIdMembershipsByOrderMembershipId({
      path: {
        encryptedOrderId: order?.encryptedId ?? '0',
        orderMembershipId
      }
    }),
    onSuccess: (data: any) => {
      setPendingRemoval(null);
      if (data.status === 204) {
        applyOrder(undefined);
      } else if (data.status !== 200) {
        toastError(data.response.data);
      } else {
        applyOrder(data.data);
      }
    },
    onError: () => {
      setPendingRemoval(null);
      unexpectedErrorToast();
    }
  });
  const removeDonationMutation = useMutation({
    mutationKey: cartOrderMutationKey,
    mutationFn: (orderDonationId: number) => deleteApiOrdersByEncryptedOrderIdDonationsByOrderDonationId({
      path: {
        encryptedOrderId: order?.encryptedId ?? '0',
        orderDonationId
      }
    }),
    onSuccess: (data: any) => {
      setPendingRemoval(null);
      if (data.status === 204) {
        applyOrder(undefined);
      } else if (data.status !== 200) {
        toastError(data.response.data);
      } else {
        applyOrder(data.data);
      }
    },
    onError: () => {
      setPendingRemoval(null);
      unexpectedErrorToast();
    }
  });

  // There is no dedicated endpoint to remove a whole subscription, so collect
  // every ticket id across the subscription group's event ticket groups and
  // delete them in one request.
  const removeSubscriptionMutation = useMutation({
    mutationKey: cartOrderMutationKey,
    mutationFn: (subscriptionTypeId: number) => {
      const group = subscriptionGroups.find(g => g.subscriptionTypeId === subscriptionTypeId);
      const ticketIds = (group?.eventTicketGroups ?? []).flatMap(etg => etg.tickets ?? []).map(ticket => ticket.id).filter((id): id is number => id !== undefined && id !== null);
      return deleteApiOrdersByEncryptedOrderIdTickets({
        path: {
          encryptedOrderId: order?.encryptedId ?? '0'
        },
        body: {
          ticketIds
        }
      });
    },
    onSuccess: (data: any) => {
      setPendingRemoval(null);
      if (data.status === 204) {
        applyOrder(undefined);
      } else if (data.status !== 200) {
        toastError(data.response.data);
      } else {
        applyOrder(data.data as OrderResponse);
      }
    },
    onError: () => {
      setPendingRemoval(null);
      unexpectedErrorToast();
    }
  });

  // The confirmed removal, expressed as the keys the split endpoint works in.
  const removalKeys = (removal: NonNullable<typeof pendingRemoval>): string[] => {
    if (removal.type === 'giftCard') {
      const giftCard = giftCards.find(g => g.giftCardId === removal.id);
      return giftCard ? [giftCardKey(giftCard)] : [];
    }
    if (removal.type === 'membership') {
      const membership = memberships.find(m => m.orderMembershipId === removal.id);
      return membership ? [membershipKey(membership)] : [];
    }
    if (removal.type === 'subscription') {
      const group = subscriptionGroups.find(g => g.subscriptionTypeId === removal.id);
      return (group?.eventTicketGroups ?? []).flatMap(etg => etg.tickets ?? []).map(ticketKey);
    }
    return getEventTickets(order, removal.id).map(ticketKey);
  };
  const handleConfirmRemoval = () => {
    if (!pendingRemoval) return;
    // A donation has no split key, so it is deleted outright in every mode.
    if (pendingRemoval.type === 'donation') {
      removeDonationMutation.mutate(pendingRemoval.id);
      return;
    }
    if (isSoftRemove && removals) {
      // A soft removal is expressed in the keys the split works in.
      const keys = removalKeys(pendingRemoval);
      // A membership holds nothing the venue has to release, so it is deleted
      // outright and the order comes back repriced. Only when it is the last
      // item left does it go through the removals, which let go of the order.
      const isLastKeptItem = getAllItemKeys(removals.keptOrder).every(key => keys.includes(key));
      if (pendingRemoval.type === 'membership' && !isLastKeptItem) {
        removeMembershipMutation.mutate(pendingRemoval.id);
        return;
      }
      if (keys.length > 0) removals.remove(keys);
      setPendingRemoval(null);
      return;
    }
    if (pendingRemoval.type === 'giftCard') {
      removeGiftCardMutation.mutate(pendingRemoval.id);
    } else if (pendingRemoval.type === 'membership') {
      removeMembershipMutation.mutate(pendingRemoval.id);
    } else if (pendingRemoval.type === 'subscription') {
      removeSubscriptionMutation.mutate(pendingRemoval.id);
    } else {
      removeEventMutation.mutate(pendingRemoval.id);
    }
  };
  const specialOfferQuery = {
    SpecialOfferId: appState.eventData?.order?.specialOfferPassword?.specialOfferId,
    SpecialOfferPassword: appState.eventData?.order?.specialOfferPassword?.password ?? undefined
  };
  const getTicketTypeOptions = (eventId: number) => {
    // A hall that opens as a map of sections is cached under its own key: it is
    // fetched without its seats, and the ticket types ride along either way.
    const seatsData = [specialOfferQuery, {
      ...specialOfferQuery,
      includeSeats: false
    }].map(query => queryClient.getQueryData(getApiEventsByEventIdSeatsQueryKey({
      path: {
        eventId
      },
      query
    })) as SeatsResponse | undefined).find(data => data?.ticketTypes?.length) as SeatsResponse | undefined;
    const cachedEvent = queryClient.getQueryData(getApiEventsByEventIdQueryKey({
      path: {
        eventId
      },
      query: specialOfferQuery
    })) as EventDto | undefined;
    const ticketTypes = seatsData?.ticketTypes ?? cachedEvent?.ticketTypes;
    const options = ticketTypes?.map(tt => ({
      value: tt.id as number,
      label: tt.name,
      disabled: !!tt.isSoldOut
    })) ?? [];

    // A change may only take the ticket types its rules allow, so the tickets
    // it holds offer no others.
    return getExchangeTicketTypeOptions(options, appState.tempExchangeData, eventId);
  };
  const navigateToViewSeats = (event: EventListItemDto, tickets: TicketDto[]) => {
    const eventId = event.id!;
    const eventGroupId = event.eventGroupID;
    appState.setTempEventReservationData({
      eventId,
      tickets,
      editingMode: 'order'
    });
    router.push(routes.eventSeats(eventGroupId!, eventId));
  };

  // Subscription events open the subscription seat picker (not the regular one)
  // so the subscription's seat and ticket-type requirements stay enforced. The
  // picker page reconstructs its reservation state from the live order, so no
  // temp data is set here.
  const navigateToViewSubscriptionSeats = (subscription: SubscriptionTypeGroupDto, event: EventListItemDto) => {
    router.push(routes.subscriptionSeats(subscription.subscriptionTypeId!, event.id!));
  };
  return <>
      <Stack spacing={2} sx={sx}>
        {events.map(event => {
        // The tickets this card renders. Selection is keyed off them so a
        // checkbox only ever covers what sits under it.
        const eventTickets = getEventTickets(order, event.id);
        return <Card variant="outlined" key={event.id} sx={[{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          position: 'relative',
          ...stickyCardSx,
          ...staticCardSx
        }, {
          p: cardPadding
        }]}>
              <StickyCardHeader inAside={inAside}
          // Reaches the card's edges so the stuck frame lines up with
          // its border, and pads its bottom by the gap it takes over.
          sx={{
            mx: -cardPadding,
            mt: -cardPadding,
            px: cardPadding,
            pt: cardPadding,
            pb: 2,
            mb: -2
          }}>
                <Stack direction={'row'} spacing={1} sx={{
              alignItems: 'center',
              justifyContent: 'space-between'
            }}>
                  {isSelecting && selection && <Checkbox checked={selection.ticketsState(eventTickets) === 'all'} indeterminate={selection.ticketsState(eventTickets) === 'some'} onChange={() => selection.toggleTickets(eventTickets)} slotProps={{
                input: {
                  'aria-label': intl.formatMessage({
                    id: "cart.selectEventItems",
                    defaultMessage: [{
                      "type": 0,
                      "value": "Select all tickets for "
                    }, {
                      "type": 1,
                      "value": "event"
                    }]
                  }, {
                    event: event.name ?? ''
                  })
                }
              }} sx={{
                flexShrink: 0
              }} />}
                  {inAside ? <CartAsideEventCardBody eventName={event.name ?? ''} eventSubtitle={event.subTitle ?? ''} eventStartsAt={event.startDate ?? ''} eventImageUrl={event.imageUrl ?? ''} hallName={event.venue?.name ?? undefined} eventTotal={getEventTicketsTotal(order, event.id)} originalEventTotal={getEventTicketsOriginalTotal(order, event.id)} /> : <EventCardBody eventId={event.id ?? 0} eventName={event.name ?? ''} eventSubtitle={event.subTitle ?? ''} eventStartsAt={event.startDate ?? ''} eventImageUrl={event.imageUrl ?? ''} hallName={event.venue?.name ?? undefined} eventTotal={getEventTicketsTotal(order, event.id)} originalEventTotal={getEventTicketsOriginalTotal(order, event.id)} imageSize={isMobile ? 64 : 80} showRemoveAction={canRemoveGroups}
              // Left to itself the card deletes the event outright. While
              // the reservation is being edited that has to come back here
              // instead, so the removal is only collected.
              onRemove={isSoftRemove ? () => handleRemove(() => setPendingRemoval({
                type: 'event',
                id: event.id ?? 0
              })) : onRemoveIntercepted} />}
                </Stack>
                {inAside && canRemoveGroups && <IconButton color="monochrome" variant="text" size="small" onClick={() => handleRemove(() => setPendingRemoval({
              type: 'event',
              id: event.id ?? 0
            }))} sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              color: 'action.active'
            }} label={intl.formatMessage({
              id: "common.remove",
              defaultMessage: [{
                "type": 0,
                "value": "Remove"
              }]
            })}>
                    <CrossIcon />
                  </IconButton>}
              </StickyCardHeader>
              {inAside && <Divider />}
              <EventTicketsAccordion defaultExpanded={!inAside && events.length === 1} tickets={eventTickets} ticketTypeOptions={getTicketTypeOptions(event.id ?? 0)} onChangeTicketType={(encryptedTicketId, ticketTypeId) => {
            changeTicketTypeMutation.mutate({
              encryptedTicketId: encryptedTicketId,
              ticketTypeId
            });
          }} inAside={inAside} showCustomerInfo={eventData.event?.requireAttendeeInformation ?? false} currencyData={{
            currencyName: appState.currency,
            currencyValue: currencyValue
          }} onRemoveTicket={canRemoveTickets ? (ticket: any) => handleRemove(() => isSoftRemove && removals ? removals.remove([ticketKey(ticket)]) : removeTicketMutation.mutate({
            ticket
          })) : undefined} renderDiscountClubMark={name => <DiscountClubMark name={name} height={2} />}
          // When only whole items may move, the event's own checkbox is
          // the only one: a ticket could not be ticked on its own.
          selectedTicketIds={selection?.wholeItemsOnly ? undefined : selection?.selectedTicketIds} onToggleTicket={isSelecting && selection && !selection.wholeItemsOnly ? ticket => selection.toggle(ticketKey(ticket)) : undefined} ticketSelectLabel={ticketSelectLabel} onShow360Ticket={(ticket: any) => setShow360Ticket(ticket)}
          // Opening the hall leaves the cart, which would drop a
          // half-finished selection, so it is offered only outside one.
          onViewSeats={inAside || !allowViewSeats || !canViewSeats ? undefined : () => navigateToViewSeats(event, eventTickets)} rightContent={<CartEventActions eventId={event.id ?? 0} inAside={!!inAside} allowAdd={allowProductChanges} />} />
              <CartEventProducts order={order} eventId={event.id ?? 0} inAside={!!inAside} allowChange={allowProductChanges} />
            </Card>;
      })}
        {subscriptionGroups.map(subscription => <CartSubscriptionCard key={subscription.subscriptionTypeId} subscription={subscription} events={allEvents} inAside={inAside} stickyHeader currencyData={{
        currencyName: appState.currency,
        currencyValue: currencyValue
      }} showCustomerInfo={eventData.event?.requireAttendeeInformation ?? false} subtitle={appState.isRenewalOrder ? <FormattedMessage id="common.subscriptionRenewal" defaultMessage={[{
        "type": 0,
        "value": "Subscription renewal"
      }]} /> : undefined} selection={selection} onRemove={canRemoveGroups ? () => handleRemove(() => onCancelOrder && subscriptionGroups.length === 1 ? onCancelOrder() : setPendingRemoval({
        type: 'subscription',
        id: subscription.subscriptionTypeId ?? 0
      })) : undefined} onShow360Ticket={ticket => setShow360Ticket(ticket)} onRemoveTicket={canRemoveTickets && isSoftRemove && removals ? ticket => handleRemove(() => removals.remove([ticketKey(ticket)])) : undefined} onViewSeats={allowViewSeats && canViewSeats ? event => navigateToViewSubscriptionSeats(subscription, event) : undefined} />)}
        {giftCards.map(giftCard => <CartExtraOrderItem key={giftCard.giftCardId} imageUrl={giftCard.imageUrl ?? ''} header={giftCard.name} subtitle={giftCard.message ? intl.formatMessage({
        id: "common.withCustomMessage",
        defaultMessage: [{
          "type": 0,
          "value": "With custom message"
        }]
      }) : undefined} footer={giftCard.ticketLayoutName} price={giftCard.price ?? 0} originalPrice={giftCard.originalPrice} inAside={inAside} onRemove={canRemoveGroups ? () => setPendingRemoval({
        type: 'giftCard',
        id: giftCard.giftCardId!
      }) : undefined} selected={!!selection?.isSelected(giftCardKey(giftCard))} onToggleSelect={isSelecting && selection ? () => selection.toggle(giftCardKey(giftCard)) : undefined} selectLabel={intl.formatMessage({
        id: "cart.selectGiftCard",
        defaultMessage: [{
          "type": 0,
          "value": "Select gift card"
        }]
      })} />)}
        {memberships.map(membership => <CartExtraOrderItem key={membership.orderMembershipId} imageUrl={membership.imageUrl ?? ''} header={membership.name} subtitle={membership.tagline} footer={membership.renewalLabel} footerDescription={stripHtmlTags(membership.renewalDescription)} price={membership.price ?? 0} originalPrice={membership.originalPrice} inAside={inAside} onRemove={canRemoveGroups ? () => setPendingRemoval({
        type: 'membership',
        id: membership.orderMembershipId!
      }) : undefined} selected={!!selection?.isSelected(membershipKey(membership))} onToggleSelect={isSelecting && selection ? () => selection.toggle(membershipKey(membership)) : undefined} selectLabel={intl.formatMessage({
        id: "cart.selectMembership",
        defaultMessage: [{
          "type": 0,
          "value": "Select membership"
        }]
      })} />)}
        {donations.map(donation => <CartExtraOrderItem key={donation.orderMembershipId} imageUrl={donation.imageUrl ?? ''} header={donation.name} price={donation.price ?? 0} inAside={inAside}
      // A donation carries no encrypted id, so the split cannot move it.
      // It is nothing the venue holds either, so even on a reservation
      // it is deleted straight away rather than hidden until checkout.
      onRemove={canRemoveGroups ? () => setPendingRemoval({
        type: 'donation',
        id: donation.orderMembershipId!
      }) : undefined} />)}
        <DeleteConfirmationDialog open={pendingRemoval !== null} onClose={() => setPendingRemoval(null)} onDelete={handleConfirmRemoval} itemType={pendingItemTypeRef.current === 'event' ? 'tickets' : pendingItemTypeRef.current} releaseFrom={isSoftRemove && pendingItemTypeRef.current !== 'donation' ? appState.isRenewalOrder ? 'renewal' : 'reservation' : undefined} loading={removeGiftCardMutation.isPending || removeMembershipMutation.isPending || removeDonationMutation.isPending || removeEventMutation.isPending || removeSubscriptionMutation.isPending} />
      </Stack>
      {show360Ticket && <Seat360Dialog hallName={''} ticket={show360Ticket} onClose={() => setShow360Ticket(null)} />}
    </>;
};